import { useCallback, useEffect, useState } from 'react';
import { HttpResponseDataType } from '@utils/httpRequestConfigs/httpRequests';
import { useMediaQuery } from '@mui/material';
import theme from '@theme/index';

import dynamic from 'next/dynamic';

const Alert = dynamic(() => import('@foxtons/fdl/components/Alert/index'));

type AlertSeverityType = 'error' | 'success' | 'warning';

interface AlertOptionsType {
  isOpen: boolean;
  message: string;
  severity: AlertSeverityType;
}

const initialAlertOptions: AlertOptionsType = {
  isOpen: false,
  message: '',
  severity: 'error'
};

interface ContactFormAlertProps {
  error: HttpResponseDataType;
  isLoading: boolean;
  responseStatus?: number;
  handleClose?: () => void;
  shouldHideSuccessToast?: boolean;
}

const SUCCESS_MESSAGE = 'Thank you! We will contact you shortly, if necessary';
const ERROR_MESSAGE = 'Something went wrong. Please try again.';

export default function ContactFormAlert({
  error,
  isLoading,
  responseStatus,
  handleClose,
  shouldHideSuccessToast = false
}: ContactFormAlertProps) {
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [alertOptions, setAlertOptions] =
    useState<AlertOptionsType>(initialAlertOptions);

  const handleCloseAlert = useCallback(() => {
    setAlertOptions((prevState) => ({ ...prevState, isOpen: false }));
    handleClose?.();
  }, [handleClose]);

  useEffect(() => {
    if (!error && !isLoading && responseStatus && !shouldHideSuccessToast) {
      setAlertOptions({
        isOpen: true,
        message: SUCCESS_MESSAGE,
        severity: 'success'
      });
    }

    if (error && !isLoading) {
      const errorMessage = ERROR_MESSAGE;

      setAlertOptions({
        isOpen: true,
        message: errorMessage,
        severity: 'error'
      });
    }

    if (isLoading) {
      handleCloseAlert();
    }

    /* eslint-disable react-hooks/exhaustive-deps  */
  }, [error, responseStatus, isLoading, shouldHideSuccessToast]);

  return (
    <Alert
      open={alertOptions.isOpen}
      severity={alertOptions.severity}
      onClose={handleCloseAlert}
      autoHideEnable
      description={alertOptions.message}
      anchorOrigin={{
        horizontal: 'right',
        vertical: isSmallScreen ? 'bottom' : 'top'
      }}
    />
  );
}
